import { computed, action } from 'mobx'
import { task } from 'mobx-task'
import { type Job } from '@/features/jobs/Job'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { type JobStore } from '@/features/jobs/JobStore'
import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'
import { Coupon } from '@/features/billing/coupons/Coupon'

/**
 * Dialog for removing applied coupons from jobs.
 */
export class RemoveCouponAsAdminDialogViewModel {
  /**
   * The dialog's modal state.
   */
  readonly modal = new ModalViewModel()

  constructor(
    private readonly _job: Job,
    private readonly _jobStore: JobStore,
    private readonly _getCoupon: () => Coupon | null,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {}

  /**
   * The applied coupon, displayed as `code-discount`.
   */
  @computed
  get couponDisplay() {
    const coupon = this._getCoupon() ?? Coupon.none

    return `${coupon.code}-${coupon.discount}`
  }

  /**
   * Shows the modal.
   */
  @action.bound
  show() {
    this.modal.show()
  }

  /**
   * Removes the coupon from the job.
   */
  removeCouponAsAdmin = task.resolved(async () => {
    await this._jobStore
      .removeCouponAsAdmin(this._job)
      .catch(this._createAlertErrorHandler('Removing the coupon failed'))

    this.modal.close()
    this._newAlert()
      .saying('Coupon removed!', 'The coupon was removed successfully.')
      .withState('success')
      .autoDismiss()
      .show()
  })
}
